import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Hash, User, Calendar, Tag, CreditCard } from 'lucide-react';
import { ThemeColors, BorderRadius, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';

interface StoreInfoCardProps {
  customerNumber: string;
  address: string;
  storeName: string;
  cityTown: string;
  storeNumber: string;
  province: string;
  postalCode: string;
  sortField: string;
  phoneNumber: string;
  salesGroup: string;
  email: string;
}

const Card = styled.div`
  background-color: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.card};
  overflow: hidden;
  border: 1px solid ${ThemeColors.border.light};
`;

const CardHeader = styled.div`
  padding: ${Spacing.md};
  background-color: ${ThemeColors.primary.main};
  color: ${ThemeColors.primary.contrastText};
  position: relative;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  font-family: ${Typography.fontFamily.secondary};
`;

const CardBody = styled.div`
  padding: ${Spacing.md};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${Spacing.md} ${Spacing.lg};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const InfoLabel = styled.div`
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  
  svg {
    color: ${ThemeColors.text.hint};
  }
`;

const InfoValue = styled.div`
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.medium};
  word-break: break-word;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${ThemeColors.border.light};
  margin: ${Spacing.sm} 0;
`;

export const StoreInfoCard: React.FC<StoreInfoCardProps> = ({
  customerNumber,
  address,
  storeName,
  cityTown,
  storeNumber,
  province,
  postalCode,
  sortField,
  phoneNumber,
  salesGroup,
  email,
}) => {
  const { t } = useTranslation();
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('storeDetails.title')}</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid>
          <InfoRow>
            <InfoLabel>
              <Hash size={16} />
              {t('storeDetails.customerNumber')}
            </InfoLabel>
            <InfoValue>{customerNumber}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <MapPin size={16} />
              {t('storeDetails.address')}
            </InfoLabel>
            <InfoValue>{address}</InfoValue>
          </InfoRow>
          
          <InfoRow>
            <InfoLabel>
              <User size={16} />
              {t('storeDetails.storeName')}
            </InfoLabel>
            <InfoValue>{storeName}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <MapPin size={16} />
              {t('storeDetails.cityTown')}
            </InfoLabel>
            <InfoValue>{cityTown}</InfoValue>
          </InfoRow>
        </InfoGrid>
        
        <Divider />
        
        <InfoGrid>
          <InfoRow>
            <InfoLabel>
              <Hash size={16} />
              {t('storeDetails.storeNumber')}
            </InfoLabel>
            <InfoValue>{storeNumber}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <MapPin size={16} />
              {t('storeDetails.province')}
            </InfoLabel>
            <InfoValue>{province}</InfoValue>
          </InfoRow>
          
          <InfoRow>
            <InfoLabel>
              <MapPin size={16} />
              {t('storeDetails.postalCode')}
            </InfoLabel>
            <InfoValue>{postalCode}</InfoValue>
          </InfoRow>
        </InfoGrid>
        
        <Divider />
        
        <InfoGrid>
          <InfoRow>
            <InfoLabel>
              <Tag size={16} />
              {t('storeDetails.sortField')}
            </InfoLabel>
            <InfoValue>{sortField}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <Phone size={16} />
              {t('storeDetails.phoneNumber')}
            </InfoLabel>
            <InfoValue>{phoneNumber}</InfoValue>
          </InfoRow>
          
          <InfoRow>
            <InfoLabel>
              <Calendar size={16} />
              {t('storeDetails.salesGroup')}
            </InfoLabel>
            <InfoValue>{salesGroup}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <Mail size={16} />
              {t('storeDetails.email')}
            </InfoLabel>
            <InfoValue>{email}</InfoValue>
          </InfoRow>
        </InfoGrid>
      </CardBody>
    </Card>
  );
};